import { useState } from "react";

const tiers = [
  {
    discount: "5%",
    amount: "$1,000",
    level: 1,
    label: "Silver",
    description: "The perfect start to your collection journey. Every piece you love, now a little more attainable.",
    saving: "$50",
    perks: ["Auto-applied discount", "Free gift wrapping", "Priority support"],
  },
  {
    discount: "7.5%",
    amount: "$1,500",
    level: 2,
    label: "Gold",
    description: "A meaningful step up. You know what you want — and you deserve to be rewarded for it.",
    saving: "$113",
    perks: ["Auto-applied discount", "Free gift wrapping", "Priority support", "Early access to new arrivals"],
  },
  {
    discount: "10%",
    amount: "$2,000",
    level: 3,
    label: "Platinum",
    description: "Discerning taste meets serious savings. This is where collecting becomes truly rewarding.",
    saving: "$200",
    perks: ["Auto-applied discount", "Free gift wrapping", "Priority support", "Early access", "Complimentary cleaning kit"],
  },
  {
    discount: "12.5%",
    amount: "$3,500",
    level: 4,
    label: "Diamond",
    description: "Reserved for the most passionate collectors. Your commitment to beauty deserves exceptional value.",
    saving: "$438",
    perks: ["Auto-applied discount", "Free gift wrapping", "Dedicated concierge", "Early access", "Complimentary cleaning kit", "Exclusive previews"],
  },
  {
    discount: "15%",
    amount: "$5,000",
    level: 5,
    label: "Elite",
    description: "The pinnacle of our savings program. The rarest level for the rarest taste.",
    saving: "$750",
    perks: ["Auto-applied discount", "Free gift wrapping", "Personal concierge", "First access to limited pieces", "Complimentary cleaning kit", "Exclusive previews", "Annual appreciation gift"],
  },
];

export default function DiscountTierResponsive() {
  const [active, setActive] = useState(0);
  const tier = tiers[active];

  return (
    <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
         className="w-full bg-[#FAF8F4] min-h-screen flex flex-col lg:flex-row lg:min-h-[560px]">

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=DM+Sans:wght@200;300;400&display=swap');

        /* ── Desktop tier list items ── */
        .tier-item {
          transition: all 0.2s ease;
          cursor: pointer;
          border-left: 2px solid transparent;
        }
        .tier-item:hover  { border-left-color: #C9A86A; background: rgba(201,168,106,0.04); }
        .tier-item.active { border-left-color: #C9A86A; background: rgba(201,168,106,0.07); }

        /* ── Mobile tab pills ── */
        .tab-pill {
          transition: all 0.2s ease;
          cursor: pointer;
          white-space: nowrap;
        }

        /* ── Right-panel animation ── */
        .detail-panel {
          animation: slideIn 0.25s cubic-bezier(0.4,0,0.2,1) forwards;
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(12px); }
          to   { opacity: 1; transform: translateX(0); }
        }

        /* ── Mobile detail animation ── */
        .detail-panel-mobile {
          animation: fadeUp 0.25s ease forwards;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .perk-item {
          animation: fadeUp 0.3s ease forwards;
        }

        /* ── Mobile tab grid ── */
        .tabs-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
        }
      `}</style>

      {/* ════════════════════════════════════════
          MOBILE LAYOUT  (< lg)
      ════════════════════════════════════════ */}
      <div className="lg:hidden flex flex-col w-full">

        {/* Mobile Header */}
        <div className="px-6 pt-10 pb-6 border-b border-[#EAE5DE]">
          <p style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.2em" }}
             className="text-[9px] uppercase text-[#B5A898] mb-2">
            Savings Program
          </p>
          <h2 className="text-[#1C1714] text-3xl font-light leading-snug">
            Volume <em>Discounts</em>
          </h2>
        </div>

        {/* Mobile Tabs — full-width 5-column grid, always fully visible */}
        <div className="tabs-grid border-b border-[#EAE5DE] bg-[#FAF8F4]">
          {tiers.map((t, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`tab-pill flex flex-col items-center justify-center py-3 px-1 border-b-2 transition-all duration-200 ${
                active === i
                  ? "border-[#C9A86A] bg-white"
                  : "border-transparent"
              }`}
            >
              <span style={{ fontFamily: "'DM Sans', sans-serif" }}
                    className={`text-[8px] uppercase tracking-wider mb-0.5 transition-colors duration-200 ${
                      active === i ? "text-[#C9A86A]" : "text-[#B5A898]"
                    }`}>
                {t.label}
              </span>
              <span className={`text-lg font-light italic leading-none transition-colors duration-200 ${
                active === i ? "text-[#1C1714]" : "text-[#C5BEB5]"
              }`}>
                {t.discount}
              </span>
            </button>
          ))}
        </div>

        {/* Mobile Detail Card */}
        <div key={`mobile-${active}`} className="detail-panel-mobile px-6 py-8 bg-white flex-1">

          {/* Discount + Save Row */}
          <div className="flex items-start justify-between mb-6 pb-6 border-b border-[#F0EBE3]">
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.15em" }}
                 className="text-[9px] uppercase text-[#B5A898] mb-1">
                Tier {tier.level} · {tier.label}
              </p>
              <span className="text-7xl font-light text-[#1C1714] leading-none italic">
                {tier.discount}
              </span>
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-xs text-[#B5A898] font-light mt-1">
                off your order
              </p>
            </div>

            <div className="flex flex-col items-end">
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-[9px] uppercase tracking-widest text-[#B5A898] mb-1">
                You save
              </p>
              <p className="text-2xl font-light text-[#C9A86A]">{tier.saving}</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-[10px] text-[#C5BEB5] font-light mt-0.5">
                on {tier.amount} order
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-[#6B5D52] text-base font-light italic leading-relaxed mb-6">
            "{tier.description}"
          </p>

          {/* Min Spend */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-[#FAF8F4] rounded-2xl">
            <div className="w-9 h-9 rounded-full bg-[#1C1714] flex items-center justify-center flex-shrink-0">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M7 1v12M1 7h12" stroke="#C9A86A" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-[9px] uppercase tracking-widest text-[#B5A898] mb-0.5">
                Minimum purchase
              </p>
              <p className="text-xl font-light text-[#1C1714]">{tier.amount}</p>
            </div>
          </div>

          {/* Perks */}
          <div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.15em" }}
               className="text-[9px] uppercase text-[#B5A898] mb-3">
              What's included
            </p>
            <div className="flex flex-col gap-2.5">
              {tier.perks.map((perk, i) => (
                <div key={i} className="perk-item flex items-center gap-2.5"
                     style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="w-1 h-1 rounded-full bg-[#C9A86A] flex-shrink-0" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}
                        className="text-xs text-[#8A7D72] font-light">
                    {perk}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Dots nav */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {tiers.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded-full transition-all duration-300 ${
                  active === i
                    ? "w-6 h-1.5 bg-[#C9A86A]"
                    : "w-1.5 h-1.5 bg-[#DDD8D0]"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Bottom note */}
        <p style={{ fontFamily: "'DM Sans', sans-serif" }}
           className="px-6 py-4 text-[10px] text-[#C5BEB5] font-light text-center bg-[#FAF8F4] border-t border-[#EAE5DE]">
          All discounts are applied automatically at checkout. No codes required.
        </p>
      </div>

      {/* ════════════════════════════════════════
          DESKTOP LAYOUT  (≥ lg) — original design
      ════════════════════════════════════════ */}

      {/* LEFT — Tier List */}
      <div className="hidden lg:flex lg:w-[38%] bg-[#FAF8F4] border-r border-[#EAE5DE] flex-col py-12 px-10">

        <div className="mb-10">
          <p style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.2em" }}
             className="text-[9px] uppercase text-[#B5A898] mb-3">
            Savings Program
          </p>
          <h2 className="text-[#1C1714] text-3xl font-light leading-snug">
            Volume<br /><em>Discounts</em>
          </h2>
        </div>

        <div className="flex flex-col flex-1">
          {tiers.map((t, i) => (
            <button
              key={i}
              className={`tier-item text-left pl-5 pr-4 py-4 ${active === i ? "active" : ""}`}
              onClick={() => setActive(i)}
              onMouseEnter={() => setActive(i)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}
                        className={`text-[9px] uppercase tracking-widest transition-colors duration-200 ${
                          active === i ? "text-[#C9A86A]" : "text-[#C5BEB5]"
                        }`}>
                    {String(t.level).padStart(2, "0")}
                  </span>
                  <span className={`text-lg font-light transition-colors duration-200 ${
                    active === i ? "text-[#1C1714]" : "text-[#8A7D72]"
                  }`}>
                    {t.label}
                  </span>
                </div>
                <span className={`text-2xl font-light italic transition-colors duration-200 ${
                  active === i ? "text-[#C9A86A]" : "text-[#C5BEB5]"
                }`}>
                  {t.discount}
                </span>
              </div>

              <div className="mt-2.5 ml-8 h-px bg-[#EAE5DE] overflow-hidden rounded-full">
                <div
                  className="h-full bg-[#C9A86A] rounded-full transition-all duration-500"
                  style={{ width: active === i ? `${(i + 1) / tiers.length * 100}%` : "0%" }}
                />
              </div>
            </button>
          ))}
        </div>

        <p style={{ fontFamily: "'DM Sans', sans-serif" }}
           className="mt-8 text-[10px] text-[#C5BEB5] font-light leading-relaxed">
          All discounts are applied automatically at checkout. No codes required.
        </p>
      </div>

      {/* RIGHT — Detail Panel */}
      <div className="hidden lg:flex lg:w-[62%] bg-white flex-col py-12 px-14">
        <div key={active} className="detail-panel flex flex-col h-full">

          <div className="flex items-start justify-between mb-8 pb-8 border-b border-[#F0EBE3]">
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.18em" }}
                 className="text-[9px] uppercase text-[#B5A898] mb-2">
                Tier {tier.level} · {tier.label}
              </p>
              <div className="flex items-end gap-3">
                <span className="text-8xl font-light text-[#1C1714] leading-none italic">
                  {tier.discount}
                </span>
                <span style={{ fontFamily: "'DM Sans', sans-serif" }}
                      className="text-sm text-[#B5A898] font-light mb-3">
                  off your order
                </span>
              </div>
            </div>

            <div className="flex flex-col items-end">
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-[9px] uppercase tracking-widest text-[#B5A898] mb-1">
                You save
              </p>
              <p className="text-3xl font-light text-[#C9A86A]">{tier.saving}</p>
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-[10px] text-[#C5BEB5] font-light mt-0.5">
                on {tier.amount} order
              </p>
            </div>
          </div>

          <p className="text-[#6B5D52] text-lg font-light italic leading-relaxed mb-8">
            "{tier.description}"
          </p>

          <div className="flex items-center gap-4 mb-8 p-5 bg-[#FAF8F4] rounded-2xl">
            <div className="w-10 h-10 rounded-full bg-[#1C1714] flex items-center justify-center flex-shrink-0">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1v12M1 7h12" stroke="#C9A86A" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div>
              <p style={{ fontFamily: "'DM Sans', sans-serif" }}
                 className="text-[9px] uppercase tracking-widest text-[#B5A898] mb-0.5">
                Minimum purchase
              </p>
              <p className="text-2xl font-light text-[#1C1714]">{tier.amount}</p>
            </div>
          </div>

          <div>
            <p style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.15em" }}
               className="text-[9px] uppercase text-[#B5A898] mb-4">
              What's included
            </p>
            <div className="grid grid-cols-2 gap-2">
              {tier.perks.map((perk, i) => (
                <div key={i} className="perk-item flex items-center gap-2.5"
                     style={{ animationDelay: `${i * 0.05}s` }}>
                  <div className="w-1 h-1 rounded-full bg-[#C9A86A] flex-shrink-0" />
                  <span style={{ fontFamily: "'DM Sans', sans-serif" }}
                        className="text-xs text-[#8A7D72] font-light">
                    {perk}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-auto pt-10 flex items-center gap-2">
            {tiers.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded-full transition-all duration-300 ${
                  active === i
                    ? "w-6 h-1.5 bg-[#C9A86A]"
                    : "w-1.5 h-1.5 bg-[#DDD8D0] hover:bg-[#B5A898]"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}